/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { CheckIcon, ChevronDownIcon, XIcon } from './icon'
import { chipVariants } from './chip'
import * as React from 'react'
import { use } from 'react'
import { cn } from '../lib/utils'
import { Input, inputContainerClass } from './input'
import { ScrollArea } from './scroll-area'

export const ComboboxContext: React.Context<{
  chipsRef: React.RefObject<Element | null> | null
  inputGroupRef: React.RefObject<Element | null> | null
  multiple: boolean
  hasChips: boolean
  setHasChips: (v: boolean) => void
}> = React.createContext<{
  chipsRef: React.RefObject<Element | null> | null
  inputGroupRef: React.RefObject<Element | null> | null
  multiple: boolean
  hasChips: boolean
  setHasChips: (v: boolean) => void
}>({
  chipsRef: null,
  inputGroupRef: null,
  multiple: false,
  hasChips: false,
  setHasChips: () => {},
})

export function Combobox<Value, Multiple extends boolean | undefined = false>(
  props: ComboboxPrimitive.Root.Props<Value, Multiple>,
): React.ReactElement {
  const chipsRef = React.useRef<Element | null>(null)
  const inputGroupRef = React.useRef<Element | null>(null)
  const [hasChips, setHasChips] = React.useState(false)
  const ctxValue = React.useMemo(
    () => ({
      chipsRef,
      inputGroupRef,
      multiple: !!props.multiple,
      hasChips,
      setHasChips,
    }),
    [props.multiple, hasChips],
  )
  return (
    <ComboboxContext.Provider value={ctxValue}>
      <ComboboxPrimitive.Root autoHighlight {...props} />
    </ComboboxContext.Provider>
  )
}

// FIX: there's a nasty warning when you focus the Combobox and then click on the chevron icon. It's due to rendering the Input (the order)
// FIX: for border color you need to use `border`. Currently `outline` is applied as you render Input.
// FIX: also Combobox like the old Input has startAddon feature (you use leading property) - fix.
// FIX: probably due to startAddon issue, the spacings of the icons and the input are not symmetric (type a long text to see the difference).

export function ComboboxChipsInput({
  className,
  size,
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size'> & {
  size?: 'sm' | 'default' | 'lg' | number
  ref?: React.Ref<HTMLInputElement>
}): React.ReactElement {
  const sizeValue = size ?? 'default'

  return (
    <ComboboxPrimitive.Input
      className={cn(
        'min-w-12 flex-1 text-base outline-none sm:text-sm',
        className,
      )}
      data-size={typeof sizeValue === 'string' ? sizeValue : undefined}
      data-slot="combobox-chips-input"
      size={typeof sizeValue === 'number' ? sizeValue : undefined}
      {...props}
    />
  )
}

export function ComboboxInput({
  className,
  showTrigger = true,
  showClear = false,
  startAddon,
  size,
  triggerProps,
  clearProps,
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size'> & {
  showTrigger?: boolean
  showClear?: boolean
  startAddon?: React.ReactNode
  size?: 'sm' | 'default' | 'lg' | number
  ref?: React.Ref<HTMLInputElement>
  triggerProps?: ComboboxPrimitive.Trigger.Props
  clearProps?: ComboboxPrimitive.Clear.Props
}): React.ReactElement {
  const { inputGroupRef } = use(ComboboxContext)
  const sizeValue = size ?? 'default'

  return (
    <ComboboxPrimitive.InputGroup
      className="text-on-surface relative w-full not-has-[>*.w-full]:w-fit has-disabled:opacity-64"
      data-slot="combobox-input-group"
      ref={inputGroupRef as React.Ref<HTMLDivElement> | null}
      onClick={(e) => {
        const target = e.target as HTMLElement
        if (target.closest('button, a, [role="button"]')) return
        if (target.tagName === 'INPUT') return
        const ref = inputGroupRef as React.RefObject<HTMLDivElement>
        ref.current?.querySelector<HTMLInputElement>('input')?.focus()
      }}
    >
      <ComboboxPrimitive.Input
        className={cn(className)}
        data-size={typeof sizeValue === 'string' ? sizeValue : undefined}
        data-slot="combobox-input"
        render={
          <Input
            className={cn(
              'has-disabled:opacity-100',
              (showClear || showTrigger) && 'pr-2',
            )}
            leading={startAddon}
            trailing={
              showClear || showTrigger ? (
                <>
                  {showClear && (
                    <ComboboxClear
                      className={cn(
                        "inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent opacity-80 transition-opacity outline-none hover:opacity-100 pointer-coarse:after:absolute pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
                      )}
                      {...clearProps}
                    >
                      <XIcon />
                    </ComboboxClear>
                  )}
                  {showTrigger && (
                    <ComboboxTrigger
                      className={cn(
                        "inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent opacity-80 transition-opacity outline-none hover:opacity-100 has-[+[data-slot=combobox-clear]]:hidden pointer-coarse:after:absolute pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
                      )}
                      {...triggerProps}
                    >
                      <ComboboxPrimitive.Icon data-slot="combobox-icon">
                        <ChevronDownIcon />
                      </ComboboxPrimitive.Icon>
                    </ComboboxTrigger>
                  )}
                </>
              ) : undefined
            }
          />
        }
        size={typeof sizeValue === 'number' ? sizeValue : undefined}
        {...props}
      />
    </ComboboxPrimitive.InputGroup>
  )
}

export function ComboboxTrigger({
  className,
  children,
  ...props
}: ComboboxPrimitive.Trigger.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Trigger
      className={className}
      data-slot="combobox-trigger"
      {...props}
    >
      {children}
    </ComboboxPrimitive.Trigger>
  )
}

export function ComboboxPopup({
  className,
  children,
  side = 'bottom',
  sideOffset = 4,
  alignOffset,
  align = 'start',
  anchor: anchorProp,
  small,
  ...props
}: ComboboxPrimitive.Popup.Props & {
  align?: ComboboxPrimitive.Positioner.Props['align']
  sideOffset?: ComboboxPrimitive.Positioner.Props['sideOffset']
  alignOffset?: ComboboxPrimitive.Positioner.Props['alignOffset']
  side?: ComboboxPrimitive.Positioner.Props['side']
  anchor?: ComboboxPrimitive.Positioner.Props['anchor']
  /** Dense rows: tighter item padding, smaller text. Items read it via `in-data-[small]`. */
  small?: boolean
}): React.ReactElement {
  const { chipsRef, inputGroupRef, hasChips } = use(ComboboxContext)
  const anchor = anchorProp ?? (hasChips ? chipsRef : inputGroupRef)

  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="z-50 select-none"
        data-slot="combobox-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <span
          className={cn(
            'bg-surface-3 relative flex max-h-full max-w-(--available-width) min-w-(--anchor-width) origin-(--transform-origin) rounded-[12px] transition-[scale,opacity] duration-150 ease-out not-dark:bg-clip-padding [&:has([data-ending-style])]:scale-95 [&:has([data-ending-style])]:opacity-0 [&:has([data-starting-style])]:scale-95 [&:has([data-starting-style])]:opacity-0',
            className,
          )}
        >
          <ComboboxPrimitive.Popup
            className="text-on-surface flex max-h-[min(var(--available-height),23rem)] flex-1 flex-col"
            data-slot="combobox-popup"
            data-small={small ? '' : undefined}
            {...props}
          >
            {children}
          </ComboboxPrimitive.Popup>
        </span>
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

export function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Item
      className={cn(
        "data-highlighted:bg-primary/8 data-highlighted:text-on-surface flex min-h-8 cursor-pointer items-center justify-between gap-2 rounded-md p-3 text-sm outline-none in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] data-disabled:cursor-not-allowed! data-disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
        "in-data-[small]:min-h-6 in-data-[small]:text-xs in-data-[small]:[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-slot="combobox-item"
      {...props}
    >
      <div className="flex min-w-0 items-center gap-2 truncate">{children}</div>
      <ComboboxPrimitive.ItemIndicator>
        <CheckIcon />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  )
}

export function ComboboxSeparator({
  className,
  ...props
}: ComboboxPrimitive.Separator.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Separator
      className={cn('bg-separator mx-2 my-1 h-px last:hidden', className)}
      data-slot="combobox-separator"
      {...props}
    />
  )
}

export function ComboboxGroup({
  className,
  ...props
}: ComboboxPrimitive.Group.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Group
      className={cn('[[role=group]+&]:mt-1.5', className)}
      data-slot="combobox-group"
      {...props}
    />
  )
}

export function ComboboxGroupLabel({
  className,
  ...props
}: ComboboxPrimitive.GroupLabel.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.GroupLabel
      className={cn(
        'text-on-surface-secondary px-2 py-1.5 text-center text-xs font-medium',
        className,
      )}
      data-slot="combobox-group-label"
      {...props}
    />
  )
}

export function ComboboxEmpty({
  className,
  ...props
}: ComboboxPrimitive.Empty.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Empty
      className={cn(
        'text-on-surface-secondary text-center text-sm not-empty:p-2',
        className,
      )}
      data-slot="combobox-empty"
      {...props}
    />
  )
}

export function ComboboxRow({
  className,
  ...props
}: ComboboxPrimitive.Row.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Row
      className={className}
      data-slot="combobox-row"
      {...props}
    />
  )
}

export function ComboboxValue({
  ...props
}: ComboboxPrimitive.Value.Props): React.ReactElement {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />
}

export function ComboboxList({
  className,
  ...props
}: ComboboxPrimitive.List.Props): React.ReactElement {
  return (
    <ScrollArea scrollbarGutter scrollFade>
      <ComboboxPrimitive.List
        className={cn(
          'not-empty:scroll-py-1 not-empty:px-1 not-empty:py-1 in-data-has-overflow-y:pe-3',
          className,
        )}
        data-slot="combobox-list"
        {...props}
      />
    </ScrollArea>
  )
}

export function ComboboxClear({
  className,
  ...props
}: ComboboxPrimitive.Clear.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Clear
      className={className}
      data-slot="combobox-clear"
      {...props}
    />
  )
}

export function ComboboxStatus({
  className,
  ...props
}: ComboboxPrimitive.Status.Props): React.ReactElement {
  return (
    <ComboboxPrimitive.Status
      className={cn(
        'text-on-surface-secondary px-3 py-2 text-xs font-medium empty:m-0 empty:p-0',
        className,
      )}
      data-slot="combobox-status"
      {...props}
    />
  )
}

export function ComboboxCollection(
  props: ComboboxPrimitive.Collection.Props,
): React.ReactElement {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  )
}

export function ComboboxChips({
  className,
  children,
  startAddon,
  ...props
}: ComboboxPrimitive.Chips.Props & {
  startAddon?: React.ReactNode
}): React.ReactElement {
  const { chipsRef, setHasChips } = use(ComboboxContext)

  React.useEffect(() => {
    setHasChips(true)
    return () => {
      setHasChips(false)
    }
  }, [setHasChips])

  return (
    <ComboboxPrimitive.Chips
      className={cn(
        inputContainerClass,
        'flex-wrap items-center gap-2 py-4',
        className,
      )}
      data-slot="combobox-chips"
      ref={chipsRef as React.Ref<HTMLDivElement> | null}
      onClick={(e) => {
        const target = e.target as HTMLElement
        if (target.closest('button, a, [role="button"]')) return
        if (target.tagName === 'INPUT') return
        const ref = chipsRef as React.RefObject<HTMLDivElement>
        ref.current?.querySelector<HTMLInputElement>('input')?.focus()
      }}
      {...props}
    >
      {startAddon && (
        <div
          aria-hidden="true"
          className="flex shrink-0 items-center ps-2 opacity-80 has-[+[data-slot=combobox-chip]]:pe-2 has-[~[data-size=sm]]:ps-1.5 has-[~[data-size=sm]]:has-[+[data-slot=combobox-chip]]:pe-1.5 [&_svg]:pointer-events-none [&_svg]:-ms-0.5 [&_svg]:-me-1.5 [&_svg:not([class*='size-'])]:size-5"
          data-slot="combobox-start-addon"
        >
          {startAddon}
        </div>
      )}
      {children}
    </ComboboxPrimitive.Chips>
  )
}

export function ComboboxChip({
  children,
  removeProps,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  removeProps?: ComboboxPrimitive.ChipRemove.Props
}): React.ReactElement {
  return (
    <ComboboxPrimitive.Chip
      className={cn(chipVariants({ variant: 'input' }), 'pl-3')}
      data-slot="combobox-chip"
      {...props}
    >
      {children}
      <ComboboxChipRemove
        className="cursor-pointer p-2 [&>svg]:size-5"
        {...removeProps}
      />
    </ComboboxPrimitive.Chip>
  )
}

export function ComboboxChipRemove(
  props: ComboboxPrimitive.ChipRemove.Props,
): React.ReactElement {
  return (
    <ComboboxPrimitive.ChipRemove
      aria-label="Remove"
      className="shrink-0 px-1.5 hover:opacity-100 [&_svg:not([class*='size-'])]:size-4"
      data-slot="combobox-chip-remove"
      {...props}
    >
      <XIcon />
    </ComboboxPrimitive.ChipRemove>
  )
}

export const useComboboxFilter: typeof ComboboxPrimitive.useFilter =
  ComboboxPrimitive.useFilter

export { ComboboxPrimitive }
